import { useEffect, VFC } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';

import { LoadingProgress } from '../../layout';
import { getAppBaseUrl, login, logout } from '../../routes';
import { fromAuth, useAppDispatch } from '../../store';

/**
 * Sign-out landing page. Always loaded by a full navigation, so keycloak-js is
 * initialised here (`check-sso`) and either:
 *  - an SSO session exists → Keycloak logs it out and returns the browser to /login, or
 *  - there is none (or Keycloak failed) → we go to /login ourselves.
 *
 * The decision is made only from the resolved `doLogout` result. Never redirect from
 * render based on Redux/localStorage: that fired while the Keycloak logout navigation
 * was in flight and aborted it, leaving the SSO session alive (user got silently
 * re-logged-in and bounced to /select-account).
 */
const Logout: VFC = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const baseUrl = getAppBaseUrl();
    const loginUrl = `${baseUrl}${login.path}`;
    const goToLogin = () => window.location.replace(loginUrl);

    dispatch(fromAuth.doLogout({
      redirectUrl: `${baseUrl}${logout.path}`,
      postLogoutRedirectUrl: loginUrl,
    }))
      .then(unwrapResult)
      .then(keycloakRedirectIssued => {
        if (!keycloakRedirectIssued) goToLogin();
      })
      .catch(goToLogin);
  }, [dispatch]);

  return <LoadingProgress fullscreen />;
};

export default Logout;
